import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchFinancialDocument } from "@lib/financialDocument";
import { financialDocumentQueryKeys } from "../lib";
import { FinancialDocumentDetails } from "./FinancialDocumentDetails";
import { usePathBasedFinancialDocumentDetailsView } from "./usePathBasedFinancialDocumentDetailsView";
import { financialDocumentDetailsContext } from "./financialDocumentDetails.context";

interface Props {
    financialDocumentId: number;
    isMaximized: boolean;
    toggleMaximized: () => void;
    shouldShowNavigation: boolean;
    canGoToPrevious: boolean;
    canGoToNext: boolean;
    goToPrevious: () => void;
    goToNext: () => void;
}

export const FinancialDocumentDetailsContainer: React.FC<Props> = ({
    financialDocumentId,
    isMaximized,
    toggleMaximized,
    shouldShowNavigation,
    canGoToPrevious,
    canGoToNext,
    goToPrevious,
    goToNext,
}) => {
    const { currentView, go } = usePathBasedFinancialDocumentDetailsView();

    const { data: financialDocument } = useQuery({
        queryKey: financialDocumentQueryKeys.detail(financialDocumentId),
        queryFn: () => fetchFinancialDocument(financialDocumentId),
    });

    const contextValue = useMemo(
        () => ({
            currentView,
            changeView: go,
            isMaximized,
            toggleMaximized,
            shouldShowNavigation,
            canGoToPrevious,
            canGoToNext,
            goToPrevious,
            goToNext,
        }),
        [
            currentView,
            go,
            isMaximized,
            toggleMaximized,
            shouldShowNavigation,
            canGoToPrevious,
            canGoToNext,
            goToPrevious,
            goToNext,
        ],
    );

    if (!financialDocument) {
        return null;
    }

    return (
        <financialDocumentDetailsContext.Provider value={contextValue}>
            <FinancialDocumentDetails financialDocument={financialDocument} />
        </financialDocumentDetailsContext.Provider>
    );
};
